// Exportación (Excel / PDF) del buscador de ventas: detalle por variante (talla)
// más resúmenes agrupados por talla, marca y colección.

import { formatCurrency } from '@/lib/utils'

export interface VariantExportRow {
  product: string
  brand: string
  collections: string[]
  size: string
  variant: string
  units: number
  sales: number
  cost: number | null // costo total de la línea (costo unitario × unidades) o null si no hay costo
}

export interface ExportMeta {
  companyName: string
  period: string
  search: string
  fileBase: string // nombre base del archivo, sin extensión
}

interface GroupRow {
  key: string
  products: number
  units: number
  sales: number
  cost: number
  profit: number
  margin: number | null
  missingCost: boolean
}

const NO_COLLECTION = 'Sin colección'

const groupRows = (
  rows: VariantExportRow[],
  keysOf: (r: VariantExportRow) => string[]
): GroupRow[] => {
  const map = new Map<string, { products: Set<string>; units: number; sales: number; cost: number; missingCost: boolean }>()
  rows.forEach((r) => {
    keysOf(r).forEach((key) => {
      const g = map.get(key) ?? { products: new Set<string>(), units: 0, sales: 0, cost: 0, missingCost: false }
      g.products.add(r.product)
      g.units += r.units
      g.sales += r.sales
      if (r.cost != null) g.cost += r.cost
      else g.missingCost = true
      map.set(key, g)
    })
  })
  return Array.from(map.entries())
    .map(([key, g]) => {
      const profit = g.sales - g.cost
      return {
        key,
        products: g.products.size,
        units: g.units,
        sales: g.sales,
        cost: g.cost,
        profit,
        margin: g.sales > 0 ? (profit / g.sales) * 100 : null,
        missingCost: g.missingCost,
      }
    })
    .sort((a, b) => b.units - a.units)
}

const buildGroups = (rows: VariantExportRow[]) => ({
  bySize: groupRows(rows, (r) => [r.size]),
  byBrand: groupRows(rows, (r) => [r.brand]),
  // Un producto en varias colecciones suma en cada una de ellas
  byCollection: groupRows(rows, (r) => (r.collections.length ? r.collections : [NO_COLLECTION])),
  byProduct: groupRows(rows, (r) => [r.product]),
})

const totalsOf = (rows: VariantExportRow[]) => {
  const t = rows.reduce(
    (acc, r) => {
      acc.units += r.units
      acc.sales += r.sales
      acc.cost += r.cost ?? 0
      return acc
    },
    { units: 0, sales: 0, cost: 0 }
  )
  const profit = t.sales - t.cost
  return { ...t, profit, margin: t.sales > 0 ? (profit / t.sales) * 100 : 0 }
}

const pct = (v: number | null) => (v == null ? '—' : `${v.toFixed(1)}%`)

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

// ── Excel ──────────────────────────────────────────────────────────────────

export async function exportVariantsExcel(rows: VariantExportRow[], meta: ExportMeta) {
  const { default: ExcelJS } = await import('exceljs')
  const wb = new ExcelJS.Workbook()
  wb.creator = meta.companyName
  wb.created = new Date()

  const money = '#,##0.00'
  const totals = totalsOf(rows)
  const groups = buildGroups(rows)

  const styleHeader = (ws: import('exceljs').Worksheet) => {
    const header = ws.getRow(1)
    header.font = { bold: true, color: { argb: 'FFFFFFFF' } }
    header.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F2937' } }
    ws.views = [{ state: 'frozen', ySplit: 1 }]
    ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: ws.columnCount } }
  }

  // Resumen general
  const summary = wb.addWorksheet('Resumen')
  summary.columns = [{ width: 28 }, { width: 40 }]
  summary.addRows([
    [meta.companyName],
    ['Reporte de ventas por variantes (tallas)'],
    [],
    ['Período', meta.period],
    ['Búsqueda', meta.search || 'Todos los productos'],
    ['Generado', new Date().toLocaleString('es-ES')],
    [],
    ['Productos', groups.byProduct.length],
    ['Variantes', rows.length],
    ['Tallas distintas', groups.bySize.length],
    ['Marcas', groups.byBrand.length],
    ['Colecciones', groups.byCollection.length],
    ['Unidades', totals.units],
    ['Venta total', totals.sales],
    ['Costo total', totals.cost],
    ['Ganancia', totals.profit],
    ['Margen %', Number(totals.margin.toFixed(2))],
  ])
  summary.getRow(1).font = { bold: true, size: 16 }
  summary.getRow(2).font = { italic: true, color: { argb: 'FF6B7280' } }
  ;[14, 15, 16].forEach((r) => (summary.getCell(r, 2).numFmt = money))
  for (let r = 4; r <= 17; r++) summary.getCell(r, 1).font = { bold: true }

  // Hojas agrupadas
  const addGroupSheet = (name: string, label: string, data: GroupRow[], note?: string) => {
    const ws = wb.addWorksheet(name)
    ws.columns = [
      { header: label, key: 'key', width: 36 },
      { header: 'Productos', key: 'products', width: 11 },
      { header: 'Unidades', key: 'units', width: 11 },
      { header: 'Venta', key: 'sales', width: 15, style: { numFmt: money } },
      { header: 'Costo', key: 'cost', width: 15, style: { numFmt: money } },
      { header: 'Ganancia', key: 'profit', width: 15, style: { numFmt: money } },
      { header: 'Margen %', key: 'margin', width: 11 },
      { header: 'Costo incompleto', key: 'missing', width: 16 },
    ]
    data.forEach((g) =>
      ws.addRow({
        ...g,
        margin: g.margin != null ? Number(g.margin.toFixed(2)) : null,
        missing: g.missingCost ? 'Sí' : '',
      })
    )
    styleHeader(ws)
    if (note) {
      ws.addRow([])
      ws.addRow([note]).font = { italic: true, color: { argb: 'FF6B7280' } }
    }
  }

  addGroupSheet('Por talla', 'Talla', groups.bySize)
  addGroupSheet('Por marca', 'Marca', groups.byBrand)
  addGroupSheet(
    'Por colección',
    'Colección',
    groups.byCollection,
    'Nota: un producto que pertenece a varias colecciones suma en cada una.'
  )
  addGroupSheet('Por producto', 'Producto', groups.byProduct)

  // Detalle por variante
  const detail = wb.addWorksheet('Detalle variantes')
  detail.columns = [
    { header: 'Producto', key: 'product', width: 40 },
    { header: 'Marca', key: 'brand', width: 20 },
    { header: 'Colecciones', key: 'collections', width: 32 },
    { header: 'Talla', key: 'size', width: 12 },
    { header: 'Variante', key: 'variant', width: 22 },
    { header: 'Unidades', key: 'units', width: 11 },
    { header: 'Venta', key: 'sales', width: 15, style: { numFmt: money } },
    { header: 'Costo', key: 'cost', width: 15, style: { numFmt: money } },
    { header: 'Ganancia', key: 'profit', width: 15, style: { numFmt: money } },
    { header: 'Margen %', key: 'margin', width: 11 },
  ]
  rows.forEach((r) => {
    const profit = r.cost != null ? r.sales - r.cost : null
    detail.addRow({
      ...r,
      collections: r.collections.join(', ') || NO_COLLECTION,
      profit,
      margin: profit != null && r.sales > 0 ? Number(((profit / r.sales) * 100).toFixed(2)) : null,
    })
  })
  styleHeader(detail)

  const buffer = await wb.xlsx.writeBuffer()
  downloadBlob(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `${meta.fileBase}.xlsx`
  )
}

// ── PDF ────────────────────────────────────────────────────────────────────

export async function exportVariantsPdf(rows: VariantExportRow[], meta: ExportMeta) {
  const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ])
  const pdf = new jsPDF({ orientation: 'landscape' })
  const totals = totalsOf(rows)
  const groups = buildGroups(rows)
  const margin = { left: 14, right: 14 }
  const lastY = () => (pdf as any).lastAutoTable?.finalY ?? 40

  pdf.setFontSize(18); pdf.setTextColor(40, 40, 40); pdf.text(meta.companyName, 14, 18)
  pdf.setFontSize(11); pdf.setTextColor(100, 100, 100)
  pdf.text('Ventas por variantes (tallas)', 14, 25)
  pdf.text(`Período: ${meta.period}  ·  Búsqueda: ${meta.search || 'Todos los productos'}`, 14, 31)

  // Descripción general
  autoTable(pdf, {
    startY: 37,
    head: [['Productos', 'Variantes', 'Tallas', 'Marcas', 'Colecciones', 'Unidades', 'Venta', 'Costo', 'Ganancia', 'Margen']],
    body: [[
      groups.byProduct.length, rows.length, groups.bySize.length, groups.byBrand.length,
      groups.byCollection.length, totals.units.toLocaleString('es-ES'),
      formatCurrency(totals.sales), formatCurrency(totals.cost), formatCurrency(totals.profit),
      pct(totals.margin),
    ]],
    theme: 'grid', headStyles: { fillColor: [31, 41, 55] }, margin,
  })

  const groupSection = (title: string, label: string, data: GroupRow[], color: [number, number, number]) => {
    let y = lastY() + 10
    if (y > pdf.internal.pageSize.getHeight() - 30) { pdf.addPage(); y = 18 }
    pdf.setFontSize(13); pdf.setTextColor(40, 40, 40); pdf.text(title, 14, y)
    autoTable(pdf, {
      startY: y + 3,
      head: [[label, 'Productos', 'Unidades', 'Venta', 'Costo', 'Ganancia', 'Margen']],
      body: data.map((g) => [
        g.key, g.products, g.units.toLocaleString('es-ES'), formatCurrency(g.sales),
        g.missingCost ? `${formatCurrency(g.cost)}*` : formatCurrency(g.cost),
        formatCurrency(g.profit), pct(g.margin),
      ]),
      theme: 'striped', headStyles: { fillColor: color }, margin,
      columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' } },
    })
  }

  groupSection('Agrupado por talla', 'Talla', groups.bySize, [59, 130, 246])
  groupSection('Agrupado por marca', 'Marca', groups.byBrand, [16, 185, 129])
  groupSection('Agrupado por colección', 'Colección', groups.byCollection, [168, 85, 247])

  // Detalle por variante
  pdf.addPage()
  pdf.setFontSize(13); pdf.setTextColor(40, 40, 40); pdf.text('Detalle por producto y talla', 14, 18)
  autoTable(pdf, {
    startY: 21,
    head: [['Producto', 'Marca', 'Colecciones', 'Talla', 'Unidades', 'Venta', 'Costo', 'Ganancia']],
    body: rows.map((r) => [
      r.product, r.brand, r.collections.join(', ') || NO_COLLECTION, r.size,
      r.units.toLocaleString('es-ES'), formatCurrency(r.sales),
      r.cost != null ? formatCurrency(r.cost) : '—',
      r.cost != null ? formatCurrency(r.sales - r.cost) : '—',
    ]),
    theme: 'striped', headStyles: { fillColor: [31, 41, 55] }, margin,
    styles: { fontSize: 8 },
    columnStyles: { 0: { cellWidth: 70 }, 2: { cellWidth: 55 }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' } },
  })

  let y = lastY() + 8
  if (y > pdf.internal.pageSize.getHeight() - 12) { pdf.addPage(); y = 18 }
  pdf.setFontSize(8); pdf.setTextColor(120, 120, 120)
  pdf.text('* Costo incompleto: algunos productos no tienen costo en el catálogo. Un producto en varias colecciones suma en cada una.', 14, y)

  pdf.save(`${meta.fileBase}.pdf`)
}
